import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Navbar from "./components/Navbar";
import Popup from "./components/Popup";
import { useLocation, useNavigate } from "react-router-dom";
import useVoiceDuration from "./DurationTimer";
import LoadingScreen from "./components/LoadingScreen";
import WordCloudSection from "./components/WordCloudSection";
import RecordingControls from "./components/RecordingControls";
import TranscriptionSection from "./components/TranscriptionSection";
import ConversationPercentage from "./components/ConversationPercentage";
import FooterSection from './components/FooterSection';
import ChangingWord from "./components/ChanginWord";
import RealTimeOscillator from "./components/RealTimeOscillator ";

function ObeseWordCloud({ name = "", positiveWords = [],department }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [wordCloudData, setWordCloudData] = useState([]);
  const [translatedWords, setTranslatedWords] = useState({}); 
  const [translatedSentences, setTranslatedSentences] = useState({});
  const { patientId } = location.state;
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [recognition, setRecognition] = useState(null);
  const [error, setError] = useState(null);
  const [wordPercentage, setWordPercentage] = useState(0);
  const [negativeOnlyMessage, setNegativeOnlyMessage] = useState(false);
  const [positiveSentences, setPositiveSentences] = useState([]);
  const [wordCloudDataRefresh, setWordCloudDataRefresh] = useState([]);
  const [hasPositiveWords, setHasPositiveWords] = useState(true);
  const [positive_pred, setPositivePred] = useState([]);
  const [negative_pred, setNegativePred] = useState([]);  
  const [sentiment, setSentiment] = useState(0); // Add sentiment state


  const closePopup = () => {
    setNegativeOnlyMessage(false);
    // Clear the transcript
    setTranscript("");
  };

  const { voiceDuration, startTimer, stopTimer } = useVoiceDuration();
  const conversationPercentageRef = useRef(null);
  const [loading, setLoading] = useState(true);
  // const size = [400, 400];

  const isFetchingDataRef = useRef(false); // Ref to track if data fetching is in progress

  useEffect(() => {
    const fetchWordCloudData = async () => {
      if (isFetchingDataRef.current) {
        return; // If data fetching is already in progress, return
      }

      isFetchingDataRef.current = true; // Set data fetching to true

      try {
        const storedData = localStorage.getItem("wordCloudData");
        if (storedData) {
          // Handle cached data
          const {
            positive_pred,
            negative_pred,
            positive_sentences,
            has_positive_words,
            translated_words, // Add this line to get translated words from storage
            translated_sentences

          } = JSON.parse(storedData);
          const wordCloudWords = [...positive_pred, ...negative_pred];

          setWordCloudData(wordCloudWords);
          setPositiveSentences(positive_sentences);
          setPositivePred(positive_pred);
          setNegativePred(negative_pred);
          setHasPositiveWords(has_positive_words);
          setTranslatedWords(translated_words); // Set translated words in state
          setTranslatedSentences(translated_sentences);
          setLoading(false);
          isFetchingDataRef.current = false; // Reset data fetching status
        } else {
          // Fetch data from the API
          const {
            age,
            height,
            weight,
            sleep_duration,
            gender,
            family_history,
            tsh_level,
            bmi,
            obese_level,
            patientId,
            
      
          } = location.state;

          const response = await axios.get(
           `https://testone.conversationaily.ai/api/obese/generate_wordcloud`,
            {
              params: {
                age: age,
                height: height,
                weight: weight,
                sleep_duration: sleep_duration,
                gender:gender,
                family_history:family_history,
                tsh_level:tsh_level,
                bmi:bmi,
                obese_level:obese_level,
                patientId: patientId,
          
                
              },
            }
          );
          const {
            positive_pred,
            negative_pred,
            positive_sentences,
            translated_words,
            translated_sentences,
            has_positive_words,

          } = response.data;
          const wordCloudWords = [...positive_pred, ...negative_pred];
          setPositivePred(positive_pred);
          setNegativePred(negative_pred);
          setWordCloudData(wordCloudWords);
          setPositiveSentences(positive_sentences);
          setHasPositiveWords(has_positive_words);
          setTranslatedWords(translated_words); 
          setTranslatedSentences(translated_sentences);
          setLoading(false);
          isFetchingDataRef.current = false; // Reset data fetching status
          localStorage.setItem("wordCloudData", JSON.stringify(response.data));
        }
      } catch (error) {
        console.error("Error fetching word cloud data:", error);
        setError(error);
        isFetchingDataRef.current = false; // Reset data fetching status in case of error
      }
    };

    fetchWordCloudData();
  }, [location.state]); // Only depend on location.state for initial fetch
  


  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error("Speech recognition not supported");
      return;
    }
    const newRecognition = new SpeechRecognition();
    newRecognition.continuous = true;
    newRecognition.interimResults = true;
    setRecognition(newRecognition);

    return () => {
      newRecognition.stop();
    };
  }, []);

  useEffect(() => {
    if (recognition) {
      recognition.onresult = (event) => {
        let finalTranscript = transcript;
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript + " ";
          }
        }
        setTranscript(finalTranscript);

        if (recording) {
          calculateRealTimeSentiment(finalTranscript);
        }
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setError(event.error);
      };
    }
  }, [recognition, recording, transcript]);



  const toggleRecording = async () => {
    if (!recording) {
      recognition.start();
      setRecording(true);
      startTimer(); // Start the voice duration timer
    } else {
      recognition.stop();
      stopTimer(); // Stop the voice duration timer
      setRecording(false);
      calculateWordPercentage(transcript, positiveWords);
      conversationPercentageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const calculateRealTimeSentiment = async (transcription) => {
    try {
      const response = await axios.post(
        `https://testone.conversationaily.ai/api/obese/calculate_word_percentage`,
        {
          transcription: transcription,
        }
      );
      const sentimentValue = response.data.overall_percentage;
      setSentiment(sentimentValue);
    } catch (error) {
      console.error("Error calculating word percentage:", error);
    }
  };

  const handleSaveAndGoHome = () => {
    // handleSendSMS();
    const finalDuration = voiceDuration; // Store the final duration
    saveTranscript(transcript, finalDuration);
    navigate("/obesity");
  };

  const formatDuration = ({ hours, minutes, seconds }) => {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const calculateWordPercentage = async (transcription, positiveWords) => {
    try {
      const response = await axios.post(
        `https://testone.conversationaily.ai/api/obese/calculate_word_percentage`,
        {
          transcription: transcription,
        }
      );
      setWordPercentage(response.data.overall_percentage);

      // Check if there are no positive words
      const noPositiveWords = response.data.positive_word_count === 0;

      if (noPositiveWords) {
        setNegativeOnlyMessage(true);
      } else {
        setNegativeOnlyMessage(false);
      }
    } catch (error) {
      console.error("Error calculating word percentage:", error);
    }
  };

  const saveTranscript = async (transcript, duration) => {
    try {
      await axios.post(`https://testone.conversationaily.ai/api/obese/save_transcription`, {
        patientId: patientId,
        transcript: transcript,
        duration: duration,
      });
    } catch (error) {
      console.error("Error saving transcript:", error);
    }
  };


  return (
    <>
      {loading && <LoadingScreen />}
      {!loading && (
    <div className="min-h-screen bg-white">
    <Navbar name={name} />
    <div className="wordcloudcontainer mx-auto">
              <div className="my-4 mx-4 flex justify-between items-center">

              </div>
              <WordCloudSection
                positiveSentences={positiveSentences}
                positiveWords={positive_pred}
                negativeWords={negative_pred}
                translatedWords={translatedWords}
                translatedSentences={translatedSentences}
              />

              <div>
                {negativeOnlyMessage && (
                  <Popup
                    message="No positive words were found in the transcript. Please try to use more positive words in your conversation."
                    onClose={closePopup}
                  />
                )}
<RealTimeOscillator sentiment={sentiment} isRecording={recording} />

                <RecordingControls
                  recording={recording}
                  toggleRecording={toggleRecording}
                  voiceDuration={voiceDuration}
                  formatDuration={formatDuration}
                  handleSaveAndGoHome={handleSaveAndGoHome}
                  sentiment={sentiment}
                />
                <RealTimeOscillator sentiment={sentiment} />

                <TranscriptionSection
                  transcript={transcript}
                  setTranscript={setTranscript}
                />

<div ref={conversationPercentageRef}>
              <ConversationPercentage
                wordPercentage={wordPercentage}
                handleSaveAndGoHome={handleSaveAndGoHome}
              />
            </div>
              </div>
            </div>

  </div>
      )}
    </>
  );
}

export default ObeseWordCloud;
